import { useState } from "react";
import { useFlags, type Flag } from "@/lib/queries";
import { useCreateFlag, useUpdateFlag, useDeleteFlag } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash } from "lucide-react";
import { cn } from "@/lib/utils";
import { swatchBg } from "@emr/shared/flags";

// Full user-flag color palette (2026-10-04 user ruling): the standard Tailwind
// hue set at the -100/-800/-300 shades, stored as the `flags.color` text
// column. Grays (slate/gray/zinc/neutral/stone) are deliberately excluded —
// drab gray flag dots are visually confusable with muted/disabled UI states.
// MUST mirror the server whitelist USER_FLAG_COLORS (apps/api/src/routes/flags.ts)
// — the API silently falls back to Red for off-palette strings.
const FLAG_COLORS = [
  { key: "red",      label: "Red",      classes: "bg-red-100 text-red-800 border-red-300" },
  { key: "orange",   label: "Orange",   classes: "bg-orange-100 text-orange-800 border-orange-300" },
  { key: "amber",    label: "Amber",    classes: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "yellow",   label: "Yellow",   classes: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  { key: "lime",     label: "Lime",     classes: "bg-lime-100 text-lime-800 border-lime-300" },
  { key: "green",    label: "Green",    classes: "bg-green-100 text-green-800 border-green-300" },
  { key: "emerald",  label: "Emerald",  classes: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { key: "teal",     label: "Teal",     classes: "bg-teal-100 text-teal-800 border-teal-300" },
  { key: "cyan",     label: "Cyan",     classes: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  { key: "sky",      label: "Sky",      classes: "bg-sky-100 text-sky-800 border-sky-300" },
  { key: "blue",     label: "Blue",     classes: "bg-blue-100 text-blue-800 border-blue-300" },
  { key: "indigo",   label: "Indigo",   classes: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  { key: "violet",   label: "Violet",   classes: "bg-violet-100 text-violet-800 border-violet-300" },
  { key: "purple",   label: "Purple",   classes: "bg-purple-100 text-purple-800 border-purple-300" },
  { key: "fuchsia",  label: "Fuchsia",  classes: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  { key: "pink",     label: "Pink",     classes: "bg-pink-100 text-pink-800 border-pink-300" },
  { key: "rose",     label: "Rose",     classes: "bg-rose-100 text-rose-800 border-rose-300" },
] as const;

// Admin flags are always Purple and sort above user flags.
const ADMIN_FLAG_COLOR = "bg-purple-100 text-purple-800 border-purple-300";
const DEFAULT_FLAG_COLOR = FLAG_COLORS[0].classes;

function FlagFormDialog({ open, onOpenChange, flag, isAdmin }: { open: boolean; onOpenChange: (v: boolean) => void; flag?: Flag | null; isAdmin: boolean }) {
  const [form, setForm] = useState({
    name: flag?.name ?? "",
    color: flag?.kind === "admin" ? ADMIN_FLAG_COLOR : (flag?.color ?? DEFAULT_FLAG_COLOR),
    sortOrder: flag?.sortOrder ?? 0,
    kind: flag?.kind ?? "user",
  });
  const create = useCreateFlag();
  const update = useUpdateFlag();
  const submit = async () => {
    const kind = form.kind as "user" | "admin";
    // Admin flags are always Purple and sort-first; user flags keep the
    // admin-chosen sortOrder (no longer derived from color — 2026-10-04 full
    // palette ruling; colors no longer imply priority).
    const color = kind === "admin" ? ADMIN_FLAG_COLOR : form.color;
    const sortOrder = kind === "admin" ? 0 : form.sortOrder;
    const body = { name: form.name, color, kind, sortOrder };
    try {
      if (flag) await update.mutateAsync({ id: flag.id, body });
      else await create.mutateAsync(body);
      onOpenChange(false);
    } catch { /* handled */ }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{flag ? "Edit flag" : "Add flag"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          {isAdmin && (
            <div>
              <Label>Type</Label>
              <Select
                value={form.kind}
                onValueChange={(k) => setForm({ ...form, kind: k as "user" | "admin" })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {form.kind === "user" && (
            <div>
              <Label>Color</Label>
              {/* Visual swatch grid (2026-10-04 user ruling): circular dots in
                  each palette hue, click to select; current selection ringed. */}
              <div className="flex flex-wrap gap-1.5 pt-1" role="radiogroup" aria-label="Flag color">
                {FLAG_COLORS.map((c) => {
                  const selected = form.color === c.classes;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={c.label}
                      title={c.label}
                      onClick={() => setForm({ ...form, color: c.classes })}
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded-full border-2 transition-shadow",
                        selected ? "border-maroon-700 dark:border-amber-500" : "border-transparent hover:border-black/20",
                      )}
                    >
                      <span className={cn("inline-block h-4 w-4 rounded-full border border-black/10", swatchBg(c.classes))} />
                    </button>
                  );
                })}
              </div>
              {/* Legacy DB rows with off-palette colors: none of the dots match,
                  so show which stored string the flag currently has. */}
              {!FLAG_COLORS.some((c) => c.classes === form.color) && (
                <p className="pt-1 text-xs text-muted">Current color {form.color} is off-palette; picking a swatch replaces it. Saving without picking keeps it (server falls back to Red).</p>
              )}
            </div>
          )}
          {form.kind === "user" && (
            <div>
              <Label>Sort order</Label>
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted">Lower numbers list first (after admin flags).</p>
            </div>
          )}
          {form.kind === "admin" && (
            <div className="flex items-center gap-2"><Label>Color:</Label><Badge variant={null} className={cn("border", ADMIN_FLAG_COLOR)}>Purple (admin, fixed)</Badge></div>
          )}
          <div className="flex items-center gap-2">
            <Label>Preview:</Label>
            <Badge variant={null} className={cn("border", form.kind === "admin" ? ADMIN_FLAG_COLOR : form.color)}>{form.name || "Flag"}</Badge>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!form.name || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FlagsPage() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("flags.create");
  const canEdit = isAdmin || permissions.has("flags.edit");
  const canDelete = isAdmin || permissions.has("flags.delete");
  const [editFlag, setEditFlag] = useState<Flag | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Session counter bumps on every open so <FlagFormDialog> remounts fresh each
  // time — `useState` then re-initializes from the current `flag` prop. Without
  // this, the dialog stays mounted across opens and `useState` keeps the first
  // open's values (so Edit showed a blank Name field — see SCHEMA.md §18 2026-07-20).
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditFlag(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (f: Flag) => { setEditFlag(f); setSessionId((n) => n + 1); setAddOpen(true); };
  const { data, isLoading } = useFlags();
  const deleteMut = useDeleteFlag();
  const rows = Array.isArray(data) ? data : (data as any)?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Flags Catalog</h1>
        {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}><Plus size={16} className="mr-1" /> Add Flag</Button>}
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500"><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Preview</TableHead><TableHead className="w-20">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={4}><Skeleton className="h-10" /></TableCell></TableRow> :
               rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted py-8">No flags</TableCell></TableRow> :
               rows.map((f: Flag) => (
                <TableRow key={f.id} className="even:bg-cream-50/50">
                  <TableCell className="font-medium">{f.name}</TableCell>
                  <TableCell>
                    {f.kind === "admin"
                      ? <Badge className="bg-purple-100 text-purple-800 border-purple-300 border">Admin</Badge>
                      : <Badge variant="outline" className="border-cream-300 text-muted">User</Badge>}
                  </TableCell>
                  <TableCell><Badge variant={null} className={cn("border", f.color)}>{f.name}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {/* Non-admins cannot edit admin flags. */}
                      {canEdit && (f.kind !== "admin" || isAdmin) && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(f)}><Pencil size={14} /></Button>}
                      {canDelete && (f.kind !== "admin" || isAdmin) && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteMut.mutate(f.id)}><Trash size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <FlagFormDialog key={sessionId} open={addOpen} onOpenChange={setAddOpen} flag={editFlag} isAdmin={isAdmin} />
    </div>
  );
}
